<script setup>
import PaginationControls from '#common/components/PaginationControls.vue'
import { fullURL } from '#common/utils/url'
import { pushEvent } from '#common/utils/user'

const { $auth, $store, $axios } = useNuxtApp()
const $route = useRoute()
const $router = useRouter()
const futuresProductsFetched = ref(true)
const { isLoading } = storeToRefs(useLayout())
const reviewOrder = ref(false)
const orderSuccess = ref(false)
const productDetailsSlug = ref(false)
const placedOrders = ref(null)
const responseInterceptor = ref(null)
const requestInterceptor = ref(null)
const loadingRefCount = ref(0)
// if the checkout page is talking to the following API endpoints, do not consider the page to be 'loading'
const nonBlockingEndpoints = [
  '/api/logic/esp-push/',
  '/api/logic/esp-page/',
  '/api/customer/promo_banner/',
  '/api/customer/notification/product/',
]
// Ignore these API endpoints for page loading only on initial request, subsequent requests WILL put page in loading state
const nonBlockingEndpointsInitial = [
  { url: '/api/address/pick-up-locations/ups/', initial: true },
  { url: '/api/address/pick-up-locations/fedex/', initial: true },
]

const { refresh, data: futuresData } = await useFetch('/api/catalog/futures/')

const layout = useLayout()
const metadata = useMetadata()
const loggedIn = computed(() => $auth.loggedIn)
const futuresCart = computed(() => $store.getters.futuresCart)
const setFuturesCartOpen = (open) => $store.dispatch('setFuturesCartOpen', open)
const enPrimeurEnabled = computed(
  () => metadata.constance.EP1_ENABLE === 'True',
)
const products = computed(() =>
  enPrimeurEnabled.value ? futuresData.value.results : [],
)
const partnerId = computed(() => {
  if (!loggedIn.value) return 22
  return $auth.user.partner_id
})
const totalPages = computed(() =>
  enPrimeurEnabled.value ? $store.getters.totalFuturesPages : 0,
)

function currentPageNumber() {
  return parseInt($route.query.page || '1')
}
function pageUrl(nextPageNum) {
  return '/en-primeur/?page=' + nextPageNum
}
async function displayDetailModal(product) {
  productDetailsSlug.value = product.url.match(/.*catalog\/([^/]+)\//i)[1]
}
function hideProductDetailsModal() {
  productDetailsSlug.value = false
}
function productDetailAdded() {
  hideProductDetailsModal()
  setFuturesCartOpen(true)
}
function scrollToTop() {
  setTimeout(() => {
    window.scrollTo(0, 0)
  }, 200)
}
function reviewOrderClicked() {
  setFuturesCartOpen(false)
  reviewOrder.value = true
  scrollToTop()
}
async function orderPlaced(newOrder) {
  orderSuccess.value = true
  reviewOrder.value = false
  await setFuturesCartOpen(false)
  placedOrders.value = []
  if (Array.isArray(newOrder.data)) {
    for (const chunk in newOrder.data) {
      placedOrders.value.push(newOrder.data[chunk])
    }
  } else {
    placedOrders.value.push(newOrder)
  }
  scrollToTop()
  await $store.dispatch('getFuturesCart')
}
function block(urlRequest, type) {
  let block = true
  if (urlRequest) {
    block = !nonBlockingEndpoints.find((x) => urlRequest.includes(x))
    const noBlockInitialObj = nonBlockingEndpointsInitial.find((x) =>
      urlRequest.includes(x.url),
    )
    if (noBlockInitialObj && noBlockInitialObj.initial) {
      block = false
      if (type === 'response') {
        noBlockInitialObj.initial = false
      }
    }
  }
  return block
}
function updateLoadingRequest(urlRequest) {
  if (block(urlRequest, 'request')) {
    loadingRefCount.value += 1
    isLoading.value = true
  }
}
function updateLoadingResponse(urlRequest) {
  if (block(urlRequest, 'response')) {
    const beforeLoading = isLoading.value
    // Ensure we don't ever indefinitely block loading in case there's a bug
    loadingRefCount.value = Math.max(loadingRefCount.value - 1, 0)
    isLoading.value = loadingRefCount.value > 0
    if (beforeLoading && !isLoading.value) {
      futuresProductsFetched.value = true
    }
  }
}

// Don't fetch the cart again if we did a quick add
if (!reviewOrder.value) {
  await $store.dispatch('getFuturesCart')
}

onBeforeRouteLeave(async (to, from, next) => {
  isLoading.value = true

  loadingRefCount.value = 0
  reviewOrder.value = false
  orderSuccess.value = false
  productDetailsSlug.value = false
  setFuturesCartOpen(false)

  $axios.interceptors.request.eject(requestInterceptor)
  $axios.interceptors.response.eject(responseInterceptor)

  await refresh()
  next()

  isLoading.value = false

  layout.showHeader = true
  layout.showFooter = true
})

useHead({
  title: 'En Primeur',
  meta: [
    {
      hid: 'og:title',
      name: 'og:title',
      property: 'og:title',
      content: `En Primeur`,
    },
  ],
})

watch(loggedIn, () => {
  refresh()
})

watch(futuresCart, (newFuturesCart) => {
  if (newFuturesCart.num_bottles === 0 && !orderSuccess.value) {
    reviewOrder.value = false
    setFuturesCartOpen(false)
  }
})

const prepLayoutAndLoading = () => {
  layout.showHeader = false
  layout.showFooter = false
  isLoading.value = false
  loadingRefCount.value = 0

  // Interceptors to detect API requests and show loading animation
  requestInterceptor.value = $axios.interceptors.request.use(
    (request) => {
      updateLoadingRequest(request.url)
      return request
    },
    (error) => {
      updateLoadingResponse(error.request ? error.request.url : null)
      return Promise.reject(error)
    },
  )

  responseInterceptor.value = $axios.interceptors.response.use(
    (response) => {
      updateLoadingResponse(response.config.url)
      return response
    },
    (error) => {
      updateLoadingResponse(error.response ? error.response.config.url : null)
      return Promise.reject(error)
    },
  )
}

const resetPage = async () => {
  reviewOrder.value = false
  orderSuccess.value = false
  productDetailsSlug.value = false

  await refresh()
}

onBeforeMount(prepLayoutAndLoading)
onActivated(prepLayoutAndLoading)
onMounted(async () => {
  let checkoutRedirect = false
  const quantities = {}
  const products = {}

  if ($route.query.id) {
    // Iterate over the query parameters to capture quantities and products
    for (const key in $route.query) {
      let prodOrQuantity = null
      if (key.startsWith('qty_') || key === 'qty') {
        prodOrQuantity = quantities
      } else if (key.startsWith('id_') || key === 'id') {
        prodOrQuantity = products
      }
      if (prodOrQuantity !== null) {
        try {
          const splitKey = key.split('_')
          const number = splitKey.length >= 2 ? parseInt(splitKey[1]) : 0
          prodOrQuantity[number] = parseInt($route.query[key])
        } catch (e) {
          console.error('Error parsing params', e)
        }
      }
    }

    const prodQtyData = []
    for (const number in products) {
      const qty = number in quantities ? quantities[number] : null
      prodQtyData.push({
        product_id: products[number],
        quantity: qty,
        source_url: fullURL($route),
      })
    }

    try {
      // Add to the cart
      const payload = {
        product_quantities: prodQtyData,
      }
      setTimeout(() => (reviewOrder.value = true), 100)
      loadingRefCount.value += 1
      $store.dispatch(
        'setFuturesCart',
        (
          await $axios.post(
            '/api/basket/add-multi-products-futures/',
            payload,
            {
              withCredentials: true,
              params: { checkout: 1 },
            },
          )
        ).data,
      )

      // Push the futures quick-add segment event asynchronously, the products are valid at this point
      for (const prodData of prodQtyData) {
        setTimeout(
          () =>
            pushEvent(
              {
                metric_name: 'Futures Quick Add',
                url_path: $route.fullPath,
                product: prodData.product_id,
                extra_data: {
                  quantity: prodData.quantity,
                },
              },
              $store,
            ),
          500,
        )
      }
      checkoutRedirect = true
    } catch (e) {
      let reason = null
      console.error(e)
      if (e.response && e.response.data) {
        const reasons = e.response.data.reason
        if (reasons.length > 0) {
          reason = reasons[0].reason
        }
        console.error(reason)
      }
    } finally {
      loadingRefCount.value = Math.max(loadingRefCount.value - 1, 0)
    }
  }

  // Supports the quick add by showing the cart right away and then removes the checkout query param
  if (checkoutRedirect) {
    if (futuresCart.value.num_bottles > 0) {
      await $router.replace('/en-primeur/')
    } else {
      setTimeout(() => (reviewOrder.value = false), 100)
    }
  }
})
</script>

<template>
  <div
    class="generic-redemption enprimeur-redemption"
    :class="{ 'review-order': reviewOrder }"
  >
    <div class="site-header-container simple-header">
      <header id="site-header">
        <div class="utility-navigation">
          <div class="left">
            <a v-if="reviewOrder" href="#" @click.prevent="reviewOrder = false">
              Cancel
            </a>
            <a
              v-else-if="orderSuccess && placedOrders"
              href="/en-primeur/"
              class="nuxt-link-active"
            >
              Cancel
            </a>
            <nuxt-link v-else to="/store/" class="nuxt-link-active">
              Cancel
            </nuxt-link>
          </div>
          <div class="logo">
            <nuxt-link to="/">
              <span class="sr-only">Wine Access</span>
            </nuxt-link>
          </div>
          <FuturesCart
            v-if="!reviewOrder && !orderSuccess"
            @review-order-clicked="reviewOrderClicked"
          />
        </div>
      </header>
    </div>
    <header class="section-header enprimeur">
      <h1 v-if="!reviewOrder" v-html="metadata.constance.EP1_PAGE_TITLE" />
      <h1 v-else v-html="metadata.constance.EP1_CHECKOUT_PAGE_TITLE" />
    </header>
    <ProductDetailsModalNew
      v-if="!!productDetailsSlug"
      :product-slug="productDetailsSlug"
      :partner-id="partnerId"
      :is-futures="true"
      add-type="addToFuturesCart"
      @add-to-cart="productDetailAdded"
      @hide-product-details-modal="hideProductDetailsModal"
    />
    <FuturesReview v-if="reviewOrder" @order-placed="orderPlaced" />
    <div v-else-if="orderSuccess && placedOrders">
      <div v-for="(order, index) in placedOrders" :key="order.id">
        <CheckoutThankYou
          :show-refer-a-friend="false"
          :order="order"
          :first-instance="index === 0"
          :multiple-instances="placedOrders.length > 1"
        />
      </div>
      <div class="clearfix text-center">
        <div
          class="hide-for-print text-center"
          style="display: inline-block; margin: 1em"
        >
          <a
            href="/en-primeur/"
            class="button button-solid max continue-shopping"
            style="display: flex; justify-content: center; align-items: center"
            @click.prevent="resetPage"
          >
            Shop More En Primeur
          </a>
        </div>
        <div
          class="hide-for-print text-center"
          style="display: inline-block; margin: 1em"
        >
          <nuxt-link
            href="/"
            class="button button-solid max continue-shopping"
            style="display: flex; justify-content: center; align-items: center"
          >
            Shop the Store
          </nuxt-link>
        </div>
      </div>
    </div>
    <div v-else>
      <section ref="productsSection" class="products">
        <div class="plp-nofilters">
          <div class="section-intro intro-message text-center">
            <div class="section-intro--content">
              <h2 v-html="metadata.constance.EP1_HEADLINE" />
              <p
                v-if="enPrimeurEnabled && products.length > 0"
                v-html="metadata.constance.EP1_DESCRIPTION"
              />
              <template v-else>
                <p
                  class="no-ep"
                  v-html="metadata.constance.EP1_EMPTY_PLP_NOTE"
                />
                <p
                  v-if="
                    metadata.constance.EP1_EMPTY_PLP_SHOW_BORDEAUX_BUTTON ===
                    'True'
                  "
                >
                  <nuxt-link
                    to="/store/regions/bordeaux/?ordering=-price"
                    class="button primary"
                  >
                    Shop Our Bordeaux Collection
                  </nuxt-link>
                </p>
              </template>
            </div>
          </div>
          <ProductGrid
            :items="products"
            add-button-text="Add"
            :pdp-link="false"
            :partner-id="partnerId"
            :show-heart-icon="false"
            @detail-clicked="displayDetailModal"
            @add-to-cart="setFuturesCartOpen(true)"
          />
          <PaginationControls
            v-if="futuresProductsFetched"
            :total-pages="totalPages"
            :current-page-number="currentPageNumber()"
            :url-for-page-func="pageUrl"
          />
        </div>
      </section>
    </div>
  </div>
</template>
